import React from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Props = { children: React.ReactNode };
type State = { erro: Error | null };

// Falha ao carregar o código da tela (comum logo após um deploy, quando o navegador ainda pede os arquivos antigos).
const isFalhaDeCarregamento = (erro: Error) =>
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(erro.message);

/**
 * Isola o erro de uma tela dentro da própria aba: menu, cabeçalho e as outras abas continuam funcionando,
 * em vez do portal inteiro ficar em branco.
 */
class TabErrorBoundary extends React.Component<Props, State> {
  state: State = { erro: null };

  static getDerivedStateFromError(erro: Error): State {
    return { erro };
  }

  componentDidCatch(erro: Error, info: React.ErrorInfo) {
    console.error('Erro ao exibir a tela', erro, info.componentStack);
  }

  render() {
    const { erro } = this.state;
    if (!erro) return this.props.children;

    const falhaDeCarregamento = isFalhaDeCarregamento(erro);

    return (
      <div className="mx-auto mt-12 max-w-md rounded-lg border border-[var(--app-hairline)] bg-white p-6 text-center shadow-sm">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
        <h2 className="mt-3 text-base font-semibold text-[#34343A]">
          {falhaDeCarregamento ? 'O portal foi atualizado' : 'Não foi possível abrir esta tela'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {falhaDeCarregamento
            ? 'Recarregue a página para usar a versão nova.'
            : 'Recarregue a página. Se continuar, avise o suporte com o nome da tela.'}
        </p>
        <Button className="mt-4" onClick={() => window.location.reload()}>
          <RotateCw className="mr-2 h-4 w-4" />
          Recarregar página
        </Button>
      </div>
    );
  }
}

export default TabErrorBoundary;
